import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Button, Input, Link, QryptLogoDark, QryptSymbolDark } from "@/components/ui";
import { CaptchaModal } from "@/components/waf/CaptchaModal";
import useCustomer from "@/context/customer/useCustomer";
import { useSignup } from "@/hooks/useSignup";
import { useToast } from "@/hooks/useToast";
import { AuthPageLayout } from "@/layouts/AuthPageLayout";
import { LOGIN_PAGE } from "@/router/paths";
function ErrorMissingCustomer() {
    return /*#__PURE__*/ _jsxs("div", {
        className: "min-h-screen flex flex-col items-center justify-center bg-gray-100 relative",
        children: [
            /*#__PURE__*/ _jsx(QryptSymbolDark, {
                className: "absolute inset-0 opacity-[0.03] z-0",
                style: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                }
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "bg-white p-12 rounded-lg shadow-[0_0_20px_-10px_rgb(0,0,0,0.5)] w-[440px] max-w-full flex flex-col items-center z-10",
                children: [
                    /*#__PURE__*/ _jsx(QryptLogoDark, {
                        className: "mb-6",
                        width: 160,
                        height: 48
                    }),
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-lg text-navy-60 mb-8",
                        children: "AWS Portal"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "text-center text-gray-600",
                        children: [
                            "To signup, please contact our sales team to establish a contract.",
                            /*#__PURE__*/ _jsx("br", {}),
                            /*#__PURE__*/ _jsx("br", {}),
                            "Or",
                            " ",
                            /*#__PURE__*/ _jsx(Link, {
                                href: LOGIN_PAGE,
                                underline: false,
                                children: "login"
                            }),
                            " ",
                            "if you already have an account."
                        ]
                    })
                ]
            })
        ]
    });
}
const signupSchema = z.object({
    fullName: z.string().min(1, "Full name is required"),
    email: z.string().min(1, "Email is required").email("Please enter a valid email")
});
function SignupSuccessMessage({ email }) {
    return /*#__PURE__*/ _jsx(AuthPageLayout, {
        title: "Check Your Email",
        children: /*#__PURE__*/ _jsxs("div", {
            className: "w-full text-center",
            children: [
                /*#__PURE__*/ _jsx("div", {
                    className: "mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-green-100 text-green-700",
                    children: /*#__PURE__*/ _jsx("svg", {
                        "aria-hidden": "true",
                        className: "h-7 w-7",
                        fill: "none",
                        stroke: "currentColor",
                        viewBox: "0 0 24 24",
                        children: /*#__PURE__*/ _jsx("path", {
                            d: "M5 13l4 4L19 7",
                            strokeLinecap: "round",
                            strokeLinejoin: "round",
                            strokeWidth: 2
                        })
                    })
                }),
                /*#__PURE__*/ _jsxs("p", {
                    className: "text-sm leading-6 text-gray-600",
                    children: [
                        "We've sent a temporary password to ",
                        /*#__PURE__*/ _jsx("strong", {
                            children: email
                        }),
                        "."
                    ]
                }),
                /*#__PURE__*/ _jsx("p", {
                    className: "mt-3 text-sm leading-6 text-gray-500",
                    children: "Use that temporary password to log in. You'll then be prompted to set a permanent password for your account."
                }),
                /*#__PURE__*/ _jsx("div", {
                    className: "mt-6 flex justify-center",
                    children: /*#__PURE__*/ _jsx(Button, {
                        className: "min-w-36",
                        color: "navy-90",
                        href: LOGIN_PAGE,
                        variant: "simple",
                        children: "Go to login"
                    })
                })
            ]
        })
    });
}
export default function SignupPage() {
    const { customer, resetCustomer } = useCustomer();
    const [directSignupData, setDirectSignupData] = useState(null);
    const [successEmail, setSuccessEmail] = useState(null);
    const [showCaptcha, setShowCaptcha] = useState(false);
    const [pendingSignupData, setPendingSignupData] = useState(null);
    const signupMutation = useSignup();
    const toast = useToast();
    const { register, handleSubmit, formState: { errors } } = useForm({
        resolver: zodResolver(signupSchema)
    });
    useEffect(()=>{
        // Check for direct signup parameters in URL
        const urlParams = new URLSearchParams(window.location.search);
        const customerIdentifier = urlParams.get("customerIdentifier");
        const customerAWSAccountID = urlParams.get("customerAWSAccountID");
        const productCode = urlParams.get("productCode");
        const planId = urlParams.get("planId");
        const entitlementId = urlParams.get("entitlementId");
        if (customerIdentifier && customerAWSAccountID && productCode && entitlementId) {
            const directData = {
                customerIdentifier,
                customerAWSAccountID,
                productCode,
                entitlementId
            };
            if (planId) {
                directData.planId = planId;
            }
            setDirectSignupData(directData);
            localStorage.setItem("directSignupData", JSON.stringify(directData));
            return;
        }
        // Fallback: Check for direct signup data in localStorage
        const storedDirectSignupData = localStorage.getItem("directSignupData");
        if (storedDirectSignupData) {
            try {
                const parsed = JSON.parse(storedDirectSignupData);
                setDirectSignupData(parsed);
            } catch  {
            // Failed to parse direct signup data
            }
        }
    }, []);
    const performSignup = async (signupData, captchaToken)=>{
        try {
            await signupMutation.mutateAsync({
                data: signupData,
                captchaToken
            });
            // Clear the stored direct signup data
            localStorage.removeItem("directSignupData");
            resetCustomer();
            setSuccessEmail(signupData.email);
        } catch (error) {
            if (error instanceof Error && error.message === "CAPTCHA_REQUIRED") {
                setPendingSignupData(signupData);
                setShowCaptcha(true);
            } else {
                toast.handleApiError(error, "Failed to create account");
            }
        }
    };
    const onSubmit = async (data)=>{
        if (!directSignupData) {
            toast.error("Invalid signup request. Please use the provided link.");
            return;
        }
        const signupData = {
            fullName: data.fullName,
            email: data.email,
            customerIdentifier: directSignupData.customerIdentifier,
            customerAWSAccountID: directSignupData.customerAWSAccountID,
            productCode: directSignupData.productCode,
            planId: directSignupData.planId,
            entitlementId: directSignupData.entitlementId
        };
        await performSignup(signupData);
    };
    const handleCaptchaSuccess = async (token)=>{
        setShowCaptcha(false);
        if (pendingSignupData) {
            await performSignup(pendingSignupData, token);
        }
    };
    const handleCaptchaClose = ()=>{
        setShowCaptcha(false);
        setPendingSignupData(null);
    };
    if (successEmail) {
        return /*#__PURE__*/ _jsx(SignupSuccessMessage, {
            email: successEmail
        });
    }
    if (!customer) {
        return /*#__PURE__*/ _jsx(ErrorMissingCustomer, {});
    }
    return /*#__PURE__*/ _jsxs("div", {
        className: "min-h-screen flex flex-col items-center justify-center bg-gray-100 relative",
        children: [
            /*#__PURE__*/ _jsx(QryptSymbolDark, {
                className: "absolute inset-0 opacity-[0.03] z-0",
                style: {
                    width: "100%",
                    height: "100%",
                    objectFit: "cover"
                }
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "bg-white p-12 rounded-lg shadow-[0_0_20px_-10px_rgb(0,0,0,0.5)] w-[440px] max-w-full flex flex-col items-center z-10",
                children: [
                    /*#__PURE__*/ _jsx(QryptLogoDark, {
                        className: "mb-6",
                        width: 160,
                        height: 48
                    }),
                    /*#__PURE__*/ _jsx("h2", {
                        className: "text-lg text-navy-60 mb-8",
                        children: "Create Account"
                    }),
                    directSignupData && /*#__PURE__*/ _jsx("div", {
                        className: "mb-4 text-sm text-navy-60",
                        children: "A temporary password will be sent to your email address."
                    }),
                    /*#__PURE__*/ _jsxs("form", {
                        onSubmit: handleSubmit(onSubmit),
                        className: "w-full flex flex-col gap-4",
                        children: [
                            /*#__PURE__*/ _jsx(Input, {
                                label: "Full Name",
                                type: "text",
                                placeholder: "Enter your full name",
                                ...register("fullName"),
                                errors: errors.fullName
                            }),
                            /*#__PURE__*/ _jsx(Input, {
                                label: "Email Address",
                                type: "email",
                                placeholder: "Enter your email address",
                                ...register("email"),
                                errors: errors.email
                            }),
                            /*#__PURE__*/ _jsx(Button, {
                                type: "submit",
                                variant: "simple",
                                color: "navy-90",
                                className: "w-full",
                                disabled: signupMutation.isPending,
                                children: signupMutation.isPending ? "Creating Account..." : "Create Account"
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "mt-8 text-center",
                        children: /*#__PURE__*/ _jsxs("span", {
                            className: "text-sm text-gray-600",
                            children: [
                                "Already have an account? ",
                                /*#__PURE__*/ _jsx(Link, {
                                    href: LOGIN_PAGE,
                                    children: "Log In"
                                })
                            ]
                        })
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(CaptchaModal, {
                isOpen: showCaptcha,
                onClose: handleCaptchaClose,
                onSuccess: handleCaptchaSuccess,
                onError: (error)=>toast.error(`CAPTCHA error: ${error}`)
            })
        ]
    });
}
